import { create } from 'zustand';
import {
  IproductReferenceDataInCountry,
  ISelectionOrderProductsIdsState,
  ISelectionOrderProductsIdsActions,
  ISelectedShipmentData,
  IShipmentData
} from 'types/orders';
import { LOCATION_PLACE_TYPE } from 'types/services';

interface IOrdersState extends ISelectionOrderProductsIdsState, ISelectionOrderProductsIdsActions {
  productsSelected: Pick<IproductReferenceDataInCountry, 'sku' | 'items'> | null;
  setProductsSelected: (productsSelected: Pick<IproductReferenceDataInCountry, 'sku' | 'items'> | null) => void;
  openDownloadModal: boolean;
  setOpenDownloadModal: (openDownloadModal: boolean) => void;
  resetDataWithoutLoaders: boolean;
  setResetDataWithoutLoaders: (resetDataWithoutLoaders: boolean) => void;
  resetDataWithLoaders: boolean;
  setResetDataWithLoaders: (resetDataWithLoaders: boolean) => void;
}

const useOrdersState = create<IOrdersState>((set, get) => ({
  productsSelected: null,
  setProductsSelected: productsSelected => set({ productsSelected }),
  openDownloadModal: false,
  setOpenDownloadModal: openDownloadModal => set({ openDownloadModal }),
  setIsShipmentToWarehouse: isShipmentToWarehouse => set({ isShipmentToWarehouse }),

  selectedShipmentIds: [],
  selectedShipmentsData: [],
  selectedProductIds: [],
  isAllShipmentsSelected: false,
  isShipmentToWarehouse: false,
  
  toggleShipmentSelection: (shipmentId: number, place?: string, shipmentData?: IShipmentData, hasTemporaryStorage?: boolean) => {
    const { selectedShipmentIds, selectedShipmentsData, isAllShipmentsSelected } = get();
    const isSelected = isAllShipmentsSelected || selectedShipmentIds?.includes(shipmentId);

    if (isSelected) {
      set({
        selectedShipmentIds: selectedShipmentIds?.filter(id => id !== shipmentId),
        selectedShipmentsData: selectedShipmentsData?.filter(s => s.shipmentId !== shipmentId),
        isAllShipmentsSelected: false
      });
    } else {
      const newShipmentsData =
        place && shipmentData ? [...selectedShipmentsData, { ...shipmentData, place, hasTemporaryStorage }] : selectedShipmentsData;
      set({
        selectedShipmentIds: [...selectedShipmentIds, shipmentId],
        selectedShipmentsData: newShipmentsData.filter(s => s.place !== LOCATION_PLACE_TYPE.NUDOS)
      });
    }
  },
  toggleProductSelection: (
    productId: number,
    shipmentId?: number,
    place?: string,
    allProductIdsFromShipment?: number[],
    shipmentData?: IShipmentData,
    hasTemporaryStorage?: boolean
  ) => {
    const { selectedProductIds, selectedShipmentIds, selectedShipmentsData, isAllShipmentsSelected } = get();
    const isSelected = isAllShipmentsSelected || selectedProductIds?.includes(productId);

    if (isSelected) {
      const newSelectedProductIds = selectedProductIds?.filter(id => id !== productId);
      const newShipmentsData = selectedShipmentsData?.filter(
        s => !(s.shipmentId === shipmentId && s.products.some(p => p.id === productId))
      );

      if (shipmentId !== undefined && allProductIdsFromShipment !== undefined) {
        const remainingProductsFromShipment = allProductIdsFromShipment.filter(
          id => id !== productId && newSelectedProductIds.includes(id)
        );

        if (remainingProductsFromShipment.length === 0) {
          set({
            selectedProductIds: newSelectedProductIds,
            selectedShipmentIds: selectedShipmentIds?.filter(id => id !== shipmentId),
            selectedShipmentsData: newShipmentsData,
            isAllShipmentsSelected: false,
            isShipmentToWarehouse: newShipmentsData?.some(s => s.place === LOCATION_PLACE_TYPE.NUDOS)
          });
        } else {
          set({
            selectedProductIds: newSelectedProductIds,
            selectedShipmentsData: newShipmentsData,
            isAllShipmentsSelected: false,
            isShipmentToWarehouse: newShipmentsData?.some(s => s.place === LOCATION_PLACE_TYPE.NUDOS)
          });
        }
      } else {
        set({
          selectedProductIds: newSelectedProductIds,
          selectedShipmentsData: newShipmentsData,
          isAllShipmentsSelected: false,
          isShipmentToWarehouse: newShipmentsData?.some(s => s.place === LOCATION_PLACE_TYPE.NUDOS)
        });
      }
    } else {
      const newSelectedProductIds = [...selectedProductIds, productId];

      if (shipmentId !== undefined) {
        const shipmentAlreadySelected = selectedShipmentIds?.includes(shipmentId);
        let newShipmentsData = [...selectedShipmentsData];

        if (place && shipmentData) {
          const product = shipmentData.products.find(p => p.id === productId);
          if (product) {
            const allUnitsOfProduct = shipmentData.products.filter(p => p.sku === product.sku);
            newShipmentsData.push({
              ...shipmentData,
              place,
              products: allUnitsOfProduct,
              hasTemporaryStorage
            });
          }
        }

        if (!shipmentAlreadySelected) {
          set({
            selectedProductIds: newSelectedProductIds,
            selectedShipmentIds: [...selectedShipmentIds, shipmentId],
            selectedShipmentsData: newShipmentsData,
            isShipmentToWarehouse: newShipmentsData?.some(s => s.place === LOCATION_PLACE_TYPE.NUDOS)
          });
        } else {
          set({
            selectedProductIds: newSelectedProductIds,
            selectedShipmentsData: newShipmentsData,
            isShipmentToWarehouse: newShipmentsData?.some(s => s.place === LOCATION_PLACE_TYPE.NUDOS)
          });
        }
      } else {
        set({
          selectedProductIds: newSelectedProductIds
        });
      }
    }
  },
  toggleAllShipmentsSelection: (allProductIds?: number[], shipmentsData?: ISelectedShipmentData[]) => {
    const { selectedProductIds } = get();

    if (allProductIds && allProductIds.length > 0) {
      const allSelected = allProductIds.every(id => selectedProductIds.includes(id));

      if (allSelected) {
        set({
          selectedShipmentIds: [],
          selectedShipmentsData: [],
          selectedProductIds: [],
          isAllShipmentsSelected: false,
          isShipmentToWarehouse: false
        });
      } else {
        const uniqueShipmentIds = Array?.from(
          new Set(shipmentsData?.filter(s => s.place !== LOCATION_PLACE_TYPE.NUDOS)?.map(s => s.shipmentId) || [])
        );
        set({
          selectedProductIds: allProductIds,
          selectedShipmentIds: uniqueShipmentIds,
          selectedShipmentsData: shipmentsData?.filter(s => s.place !== LOCATION_PLACE_TYPE.NUDOS) || [],
          isAllShipmentsSelected: false,
          isShipmentToWarehouse: shipmentsData?.some(s => s.place === LOCATION_PLACE_TYPE.NUDOS) || false
        });
      }
    } else {
      set({
        selectedShipmentIds: [],
        selectedShipmentsData: [],
        selectedProductIds: [],
        isAllShipmentsSelected: false,
        isShipmentToWarehouse: false
      });
    }
  },
  toggleAllProductsInShipmentSelection: (
    shipmentId: number,
    productIds: number[],
    place?: string,
    shipmentData?: IShipmentData,
    hasTemporaryStorage?: boolean,
    listOfGroupedShipments?: IShipmentData[],
  ) => {
    const { selectedProductIds, selectedShipmentIds, selectedShipmentsData } = get();
    
    const shipmentsToCheck = listOfGroupedShipments || (shipmentData ? [shipmentData] : []);
    const shipmentIdsToCheck = shipmentsToCheck.map(s => s.shipmentId);
    const allShipmentsAreSelected = shipmentIdsToCheck.every(id => selectedShipmentIds?.includes(id));

    if (allShipmentsAreSelected) {
      const shipmentsToRemove = listOfGroupedShipments?.map(s => s.shipmentId) || [shipmentId];
      const productsToRemove = listOfGroupedShipments?.flatMap(s => s.products.map(p => p.id)) || productIds;

      const newShipmentsData = selectedShipmentsData?.filter(
        s => !shipmentsToRemove.includes(s.shipmentId)
      );

      set({
        selectedProductIds: selectedProductIds?.filter(id => !productsToRemove.includes(id)),
        selectedShipmentIds: selectedShipmentIds?.filter(id => !shipmentsToRemove.includes(id)),
        selectedShipmentsData: newShipmentsData,
        isShipmentToWarehouse: newShipmentsData?.some(s => s.place === LOCATION_PLACE_TYPE.NUDOS)
      });
    } else {
      const shipmentsToAdd = listOfGroupedShipments || (shipmentData ? [shipmentData] : []);
      const productsToAdd = shipmentsToAdd.flatMap(s => s.products.map(p => p.id));
      const shipmentIdsToAdd = shipmentsToAdd.map(s => s.shipmentId);

      const newSelectedProductIds = Array.from(new Set([...selectedProductIds, ...productsToAdd]));
      const newShipmentIds = Array.from(new Set([...selectedShipmentIds, ...shipmentIdsToAdd]));

      const existingShipmentIds = new Set(selectedShipmentsData.map(s => s.shipmentId));
      const shipmentsDataToAdd: ISelectedShipmentData[] = shipmentsToAdd
        .filter(s => !existingShipmentIds.has(s.shipmentId))
        .map(s => ({
          ...s,
          place: place || s.place || '',
          hasTemporaryStorage: !!s.warehouseTemporaryStorage?.estimatedEntryDate
        }));

      const newShipmentsData = [...selectedShipmentsData, ...shipmentsDataToAdd];

      set({
        selectedProductIds: newSelectedProductIds,
        selectedShipmentIds: newShipmentIds,
        selectedShipmentsData: newShipmentsData,
        isShipmentToWarehouse: newShipmentsData?.some(s => s.place === LOCATION_PLACE_TYPE.NUDOS)
      });
    }
  },
  clearAllSelections: () => {
    set({
      selectedShipmentIds: [],
      selectedShipmentsData: [],
      selectedProductIds: [],
      isAllShipmentsSelected: false,
      isShipmentToWarehouse: false
    });
  },
  isShipmentSelected: (shipmentId: number) => {
    const { selectedShipmentIds, isAllShipmentsSelected, selectedShipmentsData } = get();
    const isShipmentToWarehouse = selectedShipmentsData?.some(s => s.place === LOCATION_PLACE_TYPE.NUDOS);

    return {
      isShipmentSelected: isAllShipmentsSelected || selectedShipmentIds?.includes(shipmentId),
      isShipmentToWarehouse
    };
  },
  isProductSelected: (productId: number) => {
    const { selectedProductIds, isAllShipmentsSelected } = get();
    return isAllShipmentsSelected || selectedProductIds?.includes(productId);
  },
  isAllProductsInShipmentSelected: (shipmentId: number, productIds: number[]) => {
    const { selectedProductIds, isAllShipmentsSelected } = get();
    if (isAllShipmentsSelected) return true;
    return productIds?.every(id => selectedProductIds?.includes(id));
  },
  someSelectedShipmentIsInPlace: (place: string) => {
    const { selectedShipmentsData } = get();
    return selectedShipmentsData.some(shipment => shipment.place === place);
  },
  hasShipmentsWithDifferentPlaces: () => {
    const { selectedShipmentsData } = get();
    if (selectedShipmentsData?.length <= 1) return { isShipmentToWarehouse: false, onlyShipmentToWarehouse: false };

    const isShipmentToWarehouse = selectedShipmentsData?.some(s => s.place === LOCATION_PLACE_TYPE.NUDOS);
    const onlyShipmentToWarehouse = selectedShipmentsData?.every(s => s.place === LOCATION_PLACE_TYPE.NUDOS);

    return {
      isShipmentToWarehouse,
      onlyShipmentToWarehouse
    };
  },
  hasShipmentsWithDifferentTemporaryStorage: () => {
    const { selectedShipmentsData } = get();
    if (selectedShipmentsData?.length === 0) return { hasMixedTemporaryStorage: false, allHaveTemporaryStorage: false, noneHaveTemporaryStorage: true };

    const shipmentsWithTemporaryStorage = selectedShipmentsData?.filter(s => s.hasTemporaryStorage === true);
    const shipmentsWithoutTemporaryStorage = selectedShipmentsData?.filter(s => s.hasTemporaryStorage === false || s.hasTemporaryStorage === undefined);

    const allHaveTemporaryStorage = shipmentsWithTemporaryStorage?.length === selectedShipmentsData?.length;
    const noneHaveTemporaryStorage = shipmentsWithoutTemporaryStorage?.length === selectedShipmentsData?.length;
    const hasMixedTemporaryStorage = shipmentsWithTemporaryStorage?.length > 0 && shipmentsWithoutTemporaryStorage?.length > 0;

    return {
      hasMixedTemporaryStorage,
      allHaveTemporaryStorage,
      noneHaveTemporaryStorage
    };
  },
  resetDataWithoutLoaders: false,
  setResetDataWithoutLoaders: resetDataWithoutLoaders => set({ resetDataWithoutLoaders }),
  resetDataWithLoaders: false,
  setResetDataWithLoaders: resetDataWithLoaders => set({ resetDataWithLoaders })
}));

export default useOrdersState;
